import {
  AppConfig,
  ReportContextProvider,
  ReportCountryProvider,
  usePermissions,
} from '@ltvco/refresh-lib/ctx';
import { Box, Grid, styled } from '@ltvco/refresh-lib/theme';
import {
  ReportLoading,
  SearchFormProps,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  SearchReportResponseData,
  reportGetters,
  SearchFooter,
} from '@ltvco/refresh-lib/v2';
import { useMutation, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { VehicleReport } from 'components/reports';
import { Sidenav } from 'components/sidenav/Sidenav';

const ReportBody = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: 'auto',
  maxWidth: '1600px',

  [theme.breakpoints.up('md')]: {
    width: '85%',
    margin: 'auto',
  },

  [theme.breakpoints.up('lg')]: {
    width: '75%',
    margin: 'auto',
  },
}));

const FooterSearchContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2.5),

  // Not only the ReportBody, but also the AppContent (App.tsx) are limited to 1600px max width
  // So with this, we make sure the footer search is always 100% of the screen width
  [theme.breakpoints.up(1600)]: {
    width: '100vw',
  },
}));

interface CommonReportProps {
  onSearchSuccess?: SearchFormProps['onSuccess'];
  onSearchError?: SearchFormProps['onError'];
}

// Allowed report types to prevent security vulnerabilities
type AllowedReportType = 'vehicle';

function ReportComponent({
  onSearchSuccess,
  onSearchError,
}: CommonReportProps) {
  const { reportType } = useParams();
  const { logError } = useContext(AppConfig);
  const queryClient = useQueryClient();
  const [queryParams] = useSearchParams();
  const permalinkQuery = queryParams.get('permalink') || '';
  const [permalink, setPermalink] = useState(permalinkQuery);
  const { session, setSession, useAccount } = useSession();
  const { refetch: refetchAccount } = useAccount(session.isAuthenticated);
  const { isFreePlan } = usePermissions();
  const [invalidVin, setInvalidVin] = useState<string>();

  // Validate reportType against allowed report types to prevent security vulnerabilities
  const getSecureReportGetter = (type: string | undefined) => {
    if (!type || !(type in reportGetters)) {
      throw new Error(`Invalid report type: ${type}`);
    }
    return reportGetters.vehicle;
  };

  const reportGetter = getSecureReportGetter(reportType);
  const loading = !permalink;

  const permalinkMutation = useMutation({
    mutationFn: () => reportGetter({ queryParams }),
    onSuccess: async (data: SearchReportResponseData) => {
      const { data: accountData } = await refetchAccount();
      setSession({
        account: accountData,
      });
      setPermalink(data.report.permalink);
    },
    onError: (error: Error, variables, context) => {
      logError('Report Route', error);

      const defaultOptions = queryClient.getDefaultOptions();

      // if mutation/query onError is overridden, we must still call the
      // global handler to make sure backend error codes are handled properly
      if (defaultOptions.mutations && defaultOptions.mutations.onError) {
        defaultOptions.mutations.onError(error, variables, context);
      }
    },
  });

  // Reset the invalid VIN whenever the report changes so a stale value can't
  // leak into the footer search on a later (valid) report.
  useEffect(() => {
    setInvalidVin(undefined);
  }, [permalink]);

  useEffect(() => {
    if (
      !permalink &&
      !permalinkMutation.isError &&
      !permalinkMutation.isLoading
    ) {
      permalinkMutation.mutate();
    } else if (permalinkQuery && permalink !== permalinkQuery) {
      setPermalink(permalinkQuery);
    }
  }, [permalink, permalinkMutation, permalinkQuery]);

  return (
    <ReportContextProvider
      permalink={permalink}
      reportType={reportType as AllowedReportType}
    >
      {loading ? (
        <ReportLoading menuItems={10} />
      ) : (
        <Grid sx={{ bgcolor: 'background.default' }} container>
          <Sidenav onReport={true} />
          <ReportBody>
            <ReportCountryProvider>
              <VehicleReport
                permalink={permalink}
                onInvalidVin={setInvalidVin}
              />
            </ReportCountryProvider>
          </ReportBody>
        </Grid>
      )}
      <FooterSearchContainer>
        <SearchFooter
          onSearchSuccess={onSearchSuccess}
          onSearchError={onSearchError}
          isFreePlan={isFreePlan}
          initialInvalidVin={invalidVin}
        />
      </FooterSearchContainer>
    </ReportContextProvider>
  );
}

const ReportWrapped: React.FC<CommonReportProps> = ({
  onSearchSuccess,
  onSearchError,
}) => {
  return (
    <ReportComponent
      onSearchSuccess={onSearchSuccess}
      onSearchError={onSearchError}
    />
  );
};

export const Report = ReportWrapped;
